import { Badge, Divider, TextBody } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { Maybe } from 'src/graphql';
import { useTheme } from 'styled-components';
import { DateFormats, formatDate } from 'src/utils/time';
import { IconWrapper } from '../Accordion/Accordion.styles';
import {
  AccordionAuthor,
  AccordionDate,
  AccordionSubtitleBadgeWrap,
  AccordionSubtitleStyled,
} from './AccordionSubtitle.styles';

export type AccordionSubtitleLiveBadgeConfig = {
  text: string;
  color: string;
  backgroundColor: string;
  width?: string;
  borderColor?: string;
};

interface Props {
  date: string;
  showError?: boolean;
  disabled?: boolean;
  author?: Maybe<string>;
  errorMessage?: string;
  showCreatedLabel?: boolean;
  id?: string;
  liveBadgeConfig?: AccordionSubtitleLiveBadgeConfig;
}

export const AccordionSubtitle = ({
  date,
  showError,
  disabled,
  author,
  errorMessage,
  showCreatedLabel = true,
  id,
  liveBadgeConfig,
}: Props) => {
  const theme = useTheme();
  const createdAt = date ? formatDate(date, DateFormats.DD_MM_YYYY) : null;

  const statusBadge = liveBadgeConfig && !showError ? liveBadgeConfig : null;
  const badgeText = statusBadge?.text ?? 'Live';
  const badgeColor =
    statusBadge?.color ?? theme.atlas.semantic.color.type.inverse;
  const badgeBackground = showError
    ? theme.atlas.semantic.color.status.error.default
    : (statusBadge?.backgroundColor ?? theme.atlas.semantic.color.type.default);

  const showLiveBadge = !showError && (Boolean(liveBadgeConfig) || !disabled);

  return (
    <AccordionSubtitleStyled
      showError={showError}
      $statusBadgeRow={Boolean(liveBadgeConfig) && Boolean(statusBadge?.width)}
    >
      {showLiveBadge && (
        <AccordionSubtitleBadgeWrap>
          <Badge
            variant="custom"
            color={badgeColor}
            background-color={badgeBackground}
            width={statusBadge?.width}
            border-color={statusBadge?.borderColor}
            text={badgeText}
            data-testid={
              id ? `accordion-live-badge-${id}` : 'accordion-live-badge'
            }
          />
        </AccordionSubtitleBadgeWrap>
      )}

      <AccordionDate data-testid="accordion-created-at">
        {showCreatedLabel && createdAt ? `Created ${createdAt}` : createdAt}
      </AccordionDate>

      <Divider
        variant="vertical"
        height="20px"
        color={theme.atlas.semantic.color.ui.divider.default}
      />
      {author && (
        <AccordionAuthor data-testid="accordion-author">
          By {author}
        </AccordionAuthor>
      )}

      {showError && (
        <>
          <IconWrapper open={true}>
            <StyledIcon
              className="uil uil-exclamation-octagon"
              color={theme.atlas.semantic.color.status.error.default}
              size={'16px'}
            />
          </IconWrapper>
          <TextBody data-testid="accordion-error-message">
            {errorMessage ?? ''}
          </TextBody>
        </>
      )}
    </AccordionSubtitleStyled>
  );
};
